import React, { useState, useEffect, useRef } from "react";
import {
  Row,
  Col,
  Drawer,
  Descriptions,
  Tag,
  Badge,
  Divider,
  Table,
  Alert,
  Statistic,
  Progress,
  Space,
  Skeleton,
  Spin,
} from "antd";
import {
  BellOutlined,
  WarningOutlined,
  LinkOutlined,
  ThunderboltOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  MinusOutlined,
  RobotOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { Card as BootstrapCard } from "reactstrap";
import { Card as AntCard } from "antd";
import { motion } from "framer-motion";
import { formatCurrency } from "../../utils/Utils";
import { MetricCard } from "./components/MetricCard";
import { ShimmerTable } from "./components/ShimmerLoader";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import {
  Block,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
} from "../../components/Component";
import { ShimmerMetricCard } from "../procurement-inventory-control-tower/components/ProcurementShimmerLoader";

// Interfaces
interface ProjectRisk {
  key: string;
  projectName: string;
  plannedTasks: number;
  delayedTasks: number;
  qualityIssues: number;
  dependencyRisks: number;
  totalRiskValue: number;
  costImpact: number;
  status: string;
  riskColor: string;
}

interface TaskRiskInterlinking {
  taskId: string;
  taskName: string;
  delayDays: number;
  projectRisk: string;
  downstreamImpact: string;
  triggeredModule: string;
  monetaryImpact: number;
  automatedMitigation: string;
  automatedMitigationSteps?: Array<{
    step: string;
    details?: string[];
    suppliers?: Array<{ name: string; onTimePercentage: number }>;
    notifications?: string[];
    costSavings?: string;
  }>;
  responsiblePerson: string;
  status: "Active" | "Mitigated" | "Pending";
}

interface PortfolioMetric {
  key: string;
  metric: string;
  currentValue: string;
  trend: "up" | "down" | "stable";
  trendValue: string;
  riskLevel: "green" | "orange" | "red";
  interpretation: string;
}

const ProjectManagementControlTower: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [riskDrawerVisible, setRiskDrawerVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectRisk | null>(
    null
  );
  const [selectedTaskRisks, setSelectedTaskRisks] = useState<
    TaskRiskInterlinking[]
  >([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [aiLoadingTasks, setAiLoadingTasks] = useState<{
    [key: string]: boolean;
  }>({});
  const [aiLoadingTexts, setAiLoadingTexts] = useState<{
    [key: string]: string;
  }>({});

  useEffect(() => {
    setTimeout(() => setLoading(false), 1500);
  }, []);

  const aiLoadingMessages = [
    "Analyzing dependencies...",
    "Fetching optimal solutions...",
    "Thinking...",
    "Processing constraints...",
    "Calculating impact...",
    "Generating recommendations...",
  ];

  // Mock data
  const mockProjectData: ProjectRisk[] = [
    {
      key: "1",
      projectName: "Grandeur Heights",
      plannedTasks: 140,
      delayedTasks: 12,
      qualityIssues: 4,
      dependencyRisks: 3,
      totalRiskValue: 3800000,
      costImpact: 8.5,
      status: "High",
      riskColor: "red",
    },
    {
      key: "2",
      projectName: "Paramount Rise",
      plannedTasks: 96,
      delayedTasks: 6,
      qualityIssues: 2,
      dependencyRisks: 1,
      totalRiskValue: 1700000,
      costImpact: 6.3,
      status: "Medium",
      riskColor: "orange",
    },
    {
      key: "3",
      projectName: "Elysium Towers",
      plannedTasks: 180,
      delayedTasks: 8,
      qualityIssues: 5,
      dependencyRisks: 2,
      totalRiskValue: 2200000,
      costImpact: 5.2,
      status: "Medium",
      riskColor: "orange",
    },
    {
      key: "4",
      projectName: "Skyline Royale",
      plannedTasks: 120,
      delayedTasks: 10,
      qualityIssues: 1,
      dependencyRisks: 2,
      totalRiskValue: 3000000,
      costImpact: 7.1,
      status: "High",
      riskColor: "red",
    },
    {
      key: "5",
      projectName: "Opéra Sky",
      plannedTasks: 60,
      delayedTasks: 3,
      qualityIssues: 0,
      dependencyRisks: 0,
      totalRiskValue: 800000,
      costImpact: 2.8,
      status: "Low",
      riskColor: "green",
    },
  ];

  const mockTaskRiskData: { [projectKey: string]: TaskRiskInterlinking[] } = {
    "1": [
      {
        taskId: "T-001",
        taskName: "Foundation Work - Block A",
        delayDays: 7,
        projectRisk: "Task Delay (>5 days)",
        downstreamImpact: "Delay in Material Procurement",
        triggeredModule: "Procurement & Work Order",
        monetaryImpact: 520000,
        automatedMitigation:
          "Adjust PR (PR-1024) with TMT Material Need-by date from 2024-03-22 to 2024-03-14 and suggested Suppliers are Shree TMT (On Time), Radha TMT (On Time); notify to Desai (procurement head)",
        automatedMitigationSteps: [
          {
            step: "Adjust Purchase Request",
            details: [
              "PR Number: PR-1024",
              "Material: TMT Steel Bars (Grade 500)",
              "Quantity Required: 350 units",
              "Original Need-by Date: 2025-10-22",
              "Suggested Need-by Date: 2025-10-14 (8 days earlier)",
            ],
          },
          {
            step: "Suggested Suppliers (Ranked by On-Time Performance)",
            suppliers: [
              { name: "Shree TMT Industries", onTimePercentage: 98 },
              { name: "Radha Steel & TMT", onTimePercentage: 96 },
              { name: "Jindal TMT Bars", onTimePercentage: 94 },
            ],
          },
          {
            step: "Auto-Notifications",
            notifications: [
              "Notify Amit Sharma (Procurement Manager) - High Priority",
              "Escalate to Desai (Procurement Head) if PO not raised by 2025-10-10",
              "Alert Project Manager about revised timeline",
            ],
          },
        ],
        responsiblePerson: "Amit Sharma (Procurement Manager)",
        status: "Active",
      },
      {
        taskId: "T-002",
        taskName: "Structural Steel Installation",
        delayDays: 9,
        projectRisk: "Dependency Failure",
        downstreamImpact: "Cascading schedule slip",
        triggeredModule: "Project Mgmt / Work Order",
        monetaryImpact: 850000,
        automatedMitigation:
          "Recalculate successor task (T-005) start date from 2024-03-18 to 2024-03-25 + lag (3 days). Update critical path and notify Priya Desai (Project Scheduler) and Vikram Singh (Project Manager) via auto-alert.",
        automatedMitigationSteps: [
          {
            step: "Identify Dependent Tasks",
            details: [
              "Current Delayed Task: T-002 (Structural Steel Installation)",
              "Anticipated Delay Duration: 9 days",
              "Successor Tasks Identified: T-005 (Plasterwork - Wing A), T-007 (MEP Rough-in)",
              "Dependency Type: Finish-to-Start (FS)",
              "Current Critical Path Status: Affected",
            ],
          },
          {
            step: "Recalculate Schedule & Critical Path",
            details: [
              "Successor Task: T-005 (Plasterwork - Wing A)",
              "Original Start Date: 2025-10-18",
              "Delay Impact: 9 days (from T-002)",
              "Lag Time: 3 days (curing/safety buffer)",
              "Revised Start Date: 2025-10-27 (18th + 9 days delay + 3 days lag - 3 days overlap)",
              "Update Gantt chart and resource calendar",
              "Recalculate critical path float for all downstream tasks",
            ],
          },
          {
            step: "Update Project Baseline & Milestone Tracking",
            details: [
              "Mark T-002 as delayed with variance documentation",
              "Update project baseline forecast",
              "Adjust milestone: 'Wing A Completion' from 2025-11-10 to 2025-11-19",
              "Trigger risk register update for cascading delays",
            ],
          },
          {
            step: "Stakeholder Notifications & Approvals",
            notifications: [
              "Auto-notify Priya Desai (Project Scheduler) - Update Schedule",
              "Alert Vikram Singh (Project Manager) - Critical Path Impact",
              "Send revised timeline to WO-2025-0012 Contractor (ABC Constructions)",
              "Escalate to PMO if delay exceeds 10 days",
            ],
          },
        ],
        responsiblePerson: "Priya Desai (Project Scheduler)",
        status: "Active",
      },
      {
        taskId: "T-003",
        taskName: "MEP Installation - Phase 1",
        delayDays: 6,
        projectRisk: "Material Shortage",
        downstreamImpact: "Idle site, extended duration",
        triggeredModule: "Inventory / Procurement",
        monetaryImpact: 680000,
        automatedMitigation:
          "Adjust PR (PR-1025) with Electrical Conduits shortfall qty (350 units) and Need-by date from 2024-03-22 to 2024-03-14. Suggested suppliers: Havells Electricals (On-Time: 98%), Polycab Wires (On-Time: 95%). Auto-notify Suresh Kumar (Procurement Head) and escalate to CFO if PO not raised by 2024-03-12.",
        automatedMitigationSteps: [
          {
            step: "Update Purchase Request",
            details: [
              "PR Number: PR-1025",
              "Material: Electrical Conduits (PVC 25mm)",
              "Shortfall Quantity: 350 units",
              "Current Stock: 120 units",
              "Total Required: 470 units",
              "Original Need-by Date: 2025-10-22",
              "Expedited Need-by Date: 2025-10-14",
            ],
          },
          {
            step: "Recommended Suppliers (Performance-Based)",
            suppliers: [
              { name: "Havells Electricals Ltd.", onTimePercentage: 98 },
              { name: "Polycab Wires Pvt. Ltd.", onTimePercentage: 95 },
              { name: "Finolex Cables", onTimePercentage: 92 },
            ],
          },
          {
            step: "Escalation & Notifications",
            notifications: [
              "Auto-notify Suresh Kumar (Procurement Head) - Urgent",
              "Send material shortage alert to Site Engineer",
              "Escalate to CFO if PO not approved by 2025-10-12",
              "Update project timeline dashboard",
            ],
          },
        ],
        responsiblePerson: "Suresh Kumar (Store In-charge)",
        status: "Pending",
      },
      {
        taskId: "T-004",
        taskName: "Concrete Pouring - Level 2",
        delayDays: 5,
        projectRisk: "Quality Defect",
        downstreamImpact: "Rework required, schedule delay",
        triggeredModule: "Quality / Work Order",
        monetaryImpact: 450000,
        automatedMitigation:
          "Block RA Bill (RA-2024-018) approval for WO-2025-0012 until quality defect (Defect ID: QD-445) is closed by 2024-03-18. Auto-generate rework task (T-004-R1) with 3-day buffer. Notify Rajesh Verma (QA Manager) and contractor (ABC Constructions).",
        automatedMitigationSteps: [
          {
            step: "Quality Defect Assessment",
            details: [
              "Defect ID: QD-445",
              "Original Task: T-004 (Concrete Pouring - Level 2)",
              "Work Order: WO-2025-0012 (ABC Constructions)",
              "Defect Type: Concrete strength test failure",
              "Affected Area: 120 sqm (Column Grid C3-C8, Level 2)",
              "Current Status: Non-conformance reported on 2025-10-10",
              "Required Action: Demolition & Re-pouring",
            ],
          },
          {
            step: "Block RA Bill Payment Until Resolution",
            details: [
              "RA Bill Number: RA-2025-018",
              "Total Bill Value: ₹12.5L",
              "Defect-Related Work Value: ₹1.8L (hold amount)",
              "Payment Hold Trigger: Automatic based on QD-445 status",
              "Hold Until: Defect closure date (Target: 2025-10-18)",
              "Approval Workflow: Blocked at QA Manager approval stage",
              "Contractor Notification: Auto-sent on 2025-10-10",
            ],
          },
          {
            step: "Auto-Generate Rework Task",
            details: [
              "New Rework Task ID: T-004-R1",
              "Task Name: Concrete Rework - Level 2 (Grid C3-C8)",
              "Scope: Demolish defective concrete (120 sqm) + Re-pour with QC",
              "Duration: 3 days (demolition: 1 day, re-pour: 1 day, curing inspection: 1 day)",
              "Buffer: 3 days added for quality re-testing",
              "Assigned To: ABC Constructions (same contractor)",
              "Scheduled Start: 2025-10-15 (upon defect confirmation)",
              "Quality Hold Point: Mandatory cube test before re-pour approval",
            ],
          },
          {
            step: "Stakeholder Notifications & Quality Workflow",
            notifications: [
              "Notify Rajesh Verma (QA Manager) - Quality NCR Raised",
              "Alert ABC Constructions - Rework Required & Payment Hold",
              "Send defect report to Project Manager (Vikram Singh)",
              "Escalate to Client QA team for joint inspection on 2025-10-14",
              "Update defect tracking dashboard with closure target: 2025-10-18",
            ],
          },
        ],
        responsiblePerson: "Rajesh Verma (QA Manager)",
        status: "Active",
      },
      {
        taskId: "T-005",
        taskName: "Plasterwork - Wing A",
        delayDays: 8,
        projectRisk: "Low Resource Utilization",
        downstreamImpact: "Idle manpower cost increasing",
        triggeredModule: "Work Order / HR",
        monetaryImpact: 1300000,
        automatedMitigation:
          "Auto-reassign idle team (Team-05: 12 workers) from delayed task (T-005) to critical task (T-002) from 2024-03-16 with 2-day mobilization buffer. Update resource histogram and notify Vikram Singh (Project Manager) and Ramesh Contractor (Site Supervisor). Estimated cost savings: ₹2.8L.",
        automatedMitigationSteps: [
          {
            step: "Resource Reallocation",
            details: [
              "Team ID: Team-05",
              "Current Assignment: T-005 (Plasterwork - Wing A) - Delayed",
              "New Assignment: T-002 (Structural Steel Installation) - Critical Path",
              "Team Size: 12 skilled workers",
              "Utilization Rate: Currently 68% → Target 95%",
              "Mobilization Date: 2025-10-16",
              "Buffer Period: 2 days for transition",
            ],
          },
          {
            step: "Update Project Resources & Documentation",
            details: [
              "Resource Histogram: Update allocation chart for T-005 (reduced from 12 to 0) and T-002 (increased from 8 to 20)",
              "Resource-Loaded Schedule: Recalculate task durations based on new crew size (T-002 duration reduced by 30%)",
              "Daily Manpower Report: Adjust headcount tracking - Team-05 (12 workers) moved from 'Plasterwork' to 'Structural Steel'",
              "Cost Center Reallocation: Transfer Team-05 labor cost (₹45,000/day) from Cost Code 'CC-Plastering' to 'CC-Structural'",
              "Productivity Tracking: Update KPIs - Target utilization increase from 68% to 95% for Team-05",
              "Equipment & Tools: Reassign scaffolding and steel fixing tools to new work area (Grid A1-A5)",
            ],
          },
          {
            step: "Stakeholder Notifications",
            notifications: [
              "Notify Vikram Singh (Project Manager) - Action Required",
              "Alert Ramesh Contractor (Site Supervisor) - Immediate",
              "Update workforce deployment dashboard",
              "Send revised task assignments to Team-05 Lead",
            ],
            costSavings: "₹2.8L (estimated idle cost avoided)",
          },
        ],
        responsiblePerson: "Vikram Singh (Project Manager)",
        status: "Active",
      },
    ],
  };

  const mockPortfolioData: PortfolioMetric[] = [
    {
      key: "1",
      metric: "Active Projects",
      currentValue: "18",
      trend: "up",
      trendValue: "+2 MoM",
      riskLevel: "green",
      interpretation: "Normal operational load",
    },
    {
      key: "2",
      metric: "Delayed Tasks",
      currentValue: "42 / 980",
      trend: "up",
      trendValue: "+8",
      riskLevel: "orange",
      interpretation: "Manageable delay zone",
    },
    {
      key: "3",
      metric: "Avg Delay Duration",
      currentValue: "9.5 days",
      trend: "up",
      trendValue: "+1.2 days",
      riskLevel: "orange",
      interpretation: "Schedule tightening needed",
    },
    {
      key: "4",
      metric: "Overall Progress",
      currentValue: "72%",
      trend: "stable",
      trendValue: "Stable",
      riskLevel: "green",
      interpretation: "On track",
    },
    {
      key: "5",
      metric: "Budget Utilized",
      currentValue: "78%",
      trend: "up",
      trendValue: "+3%",
      riskLevel: "orange",
      interpretation: "Near upper threshold",
    },
    {
      key: "6",
      metric: "Total Risk Exposure",
      currentValue: "₹1.15 Cr",
      trend: "up",
      trendValue: "+0.25 Cr",
      riskLevel: "red",
      interpretation: "Financial oversight required",
    },
  ];

  const [projectData] = useState<ProjectRisk[]>(mockProjectData);
  const [portfolioData] = useState<PortfolioMetric[]>(mockPortfolioData);

  // Calculate summary statistics
  const highRiskProjects = projectData.filter(
    (p) => p.status === "High"
  ).length;
  const mediumRiskProjects = projectData.filter(
    (p) => p.status === "Medium"
  ).length;
  const totalRiskExposure = projectData.reduce(
    (sum, p) => sum + p.totalRiskValue,
    0
  );

  const handleDelayedTasksClick = (record: ProjectRisk) => {
    setSelectedProject(record);
    const taskRisks = mockTaskRiskData[record.key] || [];
    setSelectedTaskRisks(taskRisks);
    setExpandedRowKeys([]);
    setRiskDrawerVisible(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active":
        return "error";
      case "Pending":
        return "warning";
      case "Mitigated":
        return "success";
      default:
        return "default";
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case "up":
        return <ArrowUpOutlined style={{ color: "#ff4d4f" }} />;
      case "down":
        return <ArrowDownOutlined style={{ color: "#52c41a" }} />;
      case "stable":
        return <MinusOutlined style={{ color: "#1890ff" }} />;
      default:
        return null;
    }
  };

  const getRiskBadge = (riskLevel: string) => {
    const flagMap: { [key: string]: string } = {
      green: "🟢",
      orange: "🟠",
      red: "🔴",
    };
    return <span style={{ fontSize: 16 }}>{flagMap[riskLevel]}</span>;
  };

  // Portfolio Snapshot Table Columns
  const portfolioColumns = [
    {
      title: "Metric",
      dataIndex: "metric",
      key: "metric",
      width: 220,
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: "Current Value",
      dataIndex: "currentValue",
      key: "currentValue",
      width: 150,
      align: "center" as const,
      render: (text: string) => (
        <div
          style={{
            display: "flex",
            alignItems: "right",
            justifyContent: "right",
            minHeight: "32px",
          }}
        >
          <motion.span
            style={{ fontSize: 16, fontWeight: 600 }}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
          >
            {text}
          </motion.span>
        </div>
      ),
    },
    {
      title: "Trend",
      dataIndex: "trend",
      key: "trend",
      width: 180,
      align: "center" as const,
      render: (trend: string, record: PortfolioMetric) => {
        const isPositive = trend === "up";
        const isNegative = trend === "down";
        const bgColor = isPositive
          ? "#fff2e8"
          : isNegative
          ? "#f6ffed"
          : "#e6f7ff";
        const borderColor = isPositive
          ? "#ffbb96"
          : isNegative
          ? "#95de64"
          : "#91d5ff";
        const textColor = isPositive
          ? "#ff4d4f"
          : isNegative
          ? "#52c41a"
          : "#1890ff";

        return (
          <div
            style={{
              display: "flex",
              alignItems: "right",
              justifyContent: "right",
              minHeight: "32px",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              style={{
                display: "inline-flex",
                alignItems: "right",
                padding: "4px 12px",
                borderRadius: "16px",
                backgroundColor: bgColor,
                border: `1px solid ${borderColor}`,
                fontWeight: 600,
                fontSize: "13px",
                color: textColor,
              }}
            >
              {getTrendIcon(trend)}
              <span style={{ marginLeft: 6 }}>{record.trendValue}</span>
            </motion.div>
          </div>
        );
      },
    },
    {
      title: "Risk Level",
      dataIndex: "riskLevel",
      key: "riskLevel",
      width: 100,
      align: "center" as const,
      render: (riskLevel: string) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "32px",
          }}
        >
          {getRiskBadge(riskLevel)}
        </div>
      ),
    },
    {
      title: "Interpretation",
      dataIndex: "interpretation",
      key: "interpretation",
      width: 280,
      render: (text: string) => (
        <span style={{ color: "#595959" }}>{text}</span>
      ),
    },
  ];

  // Project Risk Table Columns
  const projectRiskColumns = [
    {
      title: "Project",
      dataIndex: "projectName",
      key: "projectName",
      width: 150,
      fixed: "left" as const,
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: "Planned Tasks",
      dataIndex: "plannedTasks",
      key: "plannedTasks",
      width: 120,
      align: "center" as const,
    },
    {
      title: "Delayed Tasks",
      dataIndex: "delayedTasks",
      key: "delayedTasks",
      width: 120,
      align: "center" as const,
      render: (value: number, record: ProjectRisk) => (
        <Tag
          color={value > 10 ? "red" : value > 5 ? "orange" : "green"}
         
        >
          {value}
        </Tag>
      ),
    },
    {
      title: "Quality Issues",
      dataIndex: "qualityIssues",
      key: "qualityIssues",
      width: 120,
      align: "center" as const,
      render: (value: number) => (
        <Tag
          color={value > 3 ? "red" : value > 0 ? "orange" : "green"}
          style={{ fontWeight: 600 }}
        >
          {value}
        </Tag>
      ),
    },
    {
      title: "Dependency Risks",
      dataIndex: "dependencyRisks",
      key: "dependencyRisks",
      width: 140,
      align: "center" as const,
      render: (value: number) => (
        <Tag
          color={value > 2 ? "red" : value > 0 ? "orange" : "green"}
          style={{ fontWeight: 600 }}
        >
          {value}
        </Tag>
      ),
    },
    {
      title: "Total Risk Value",
      dataIndex: "totalRiskValue",
      key: "totalRiskValue",
      width: 160,
      align: "right" as const,
      render: (value: number, record: ProjectRisk) => (
        <motion.span
          style={{ fontWeight: 600, color: "#ff4d4f",cursor: value > 0 ? "pointer" : "default",textDecoration: value > 0 ? "underline" : "none" }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          //  style={{  fontWeight: 600,  }}
          onClick={() => value > 0 && handleDelayedTasksClick(record)}
        >
          {formatCurrency(value, "INR")}
        </motion.span>
      ),
    },
    {
      title: "% Cost Impact",
      dataIndex: "costImpact",
      key: "costImpact",
      width: 140,
      align: "center" as const,
      render: (value: number) => (
        <div>
          <Progress
            percent={value * 10}
            showInfo={false}
            strokeColor={
              value > 7 ? "#ff4d4f" : value > 3 ? "#faad14" : "#52c41a"
            }
          />
          <span style={{ fontWeight: 600 }}>{value}%</span>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      align: "center" as const,
      render: (status: string) => {
        const flagMap: { [key: string]: string } = {
          High: "🔴",
          Medium: "🟠",
          Low: "🟢",
        };
        return (
          <div
            style={{
              display: "flex",
              alignItems: "right",
              justifyContent: "right",
              gap: "8px",
              minHeight: "32px",
            }}
          >
            <span>{status}</span>
            <span style={{ fontSize: 16 }}>{flagMap[status]}</span>
          </div>
        );
      },
    },
  ];

  // Task Risk Table Columns
  const taskRiskColumns = [
    {
      title: "Task ID",
      dataIndex: "taskId",
      key: "taskId",
      width: 100,
    },
    {
      title: "Task Name",
      dataIndex: "taskName",
      key: "taskName",
      width: 200,
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: "Delay Days",
      dataIndex: "delayDays",
      key: "delayDays",
      width: 100,
      align: "center" as const,
      render: (value: number) => <Tag color="red">{value} days</Tag>,
    },
    {
      title: "Risk Type",
      dataIndex: "projectRisk",
      key: "projectRisk",
      width: 180,
      render: (text: string) => (
        <span>
          <WarningOutlined style={{ color: "#faad14", marginRight: 8 }} />
          {text}
        </span>
      ),
    },
    {
      title: "Triggered Module",
      dataIndex: "triggeredModule",
      key: "triggeredModule",
      width: 180,
      render: (text: string) => (
        <Tag color="blue" icon={<LinkOutlined />}>
          {text}
        </Tag>
      ),
    },
    {
      title: "Monetary Impact",
      dataIndex: "monetaryImpact",
      key: "monetaryImpact",
      width: 150,
      align: "right" as const,
      render: (value: number) => {
        const formattedValue = value ? formatCurrency(value, "INR") : "₹0";
        return (
          <span style={{ fontWeight: 600, color: "#ff4d4f" }}>
            {formattedValue}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      align: "center" as const,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      ),
    },
  ];

  return (
    <>
      <Head title="Project Management Control Tower" />
      <Content>
        <BlockHead size="lg">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <BlockHeadContent>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BlockTitle tag="h4">
                  Project Management Control Tower
                </BlockTitle>
              </motion.div>
              <motion.div
                className="nk-block-des"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <p>
                  Executive dashboard for portfolio health, risk exposure, and
                  strategic decision-making
                </p>
              </motion.div>
            </BlockHeadContent>
          </motion.div>
        </BlockHead>

        <Block>
          <BootstrapCard className="card-bordered">
            <div className="card-inner card-inner-lg">
              {/* Portfolio Snapshot Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                style={{ marginBottom: 32 }}
              >
                <motion.h5
                  style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                   Portfolio Snapshot
                </motion.h5>

                {loading ? (
                  <ShimmerTable />
                ) : (
                  <Table
                    columns={portfolioColumns}
                    dataSource={portfolioData}
                    pagination={false}
                    scroll={{ x: 900 }}
                    bordered
                    size="small"
                    style={{
                      background: "#fff",
                      borderRadius: 8,
                      overflow: "hidden",
                    }}
                  />
                )}
              </motion.div>

              <Divider />

              {/* Project-Level Risk Summary Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                style={{ marginBottom: 32 }}
              >
                <motion.h5
                  style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.7 }}
                >
                   Project-Level Risk Summary
                </motion.h5>

                {loading ? (
                  <ShimmerTable />
                ) : (
                  <Table
                    columns={projectRiskColumns}
                    dataSource={projectData}
                    pagination={false}
                    scroll={{ x: 1200 }}
                    bordered
                    size="small"
                    style={{
                      background: "#fff",
                      borderRadius: 8,
                      overflow: "hidden",
                    }}
                  />
                )}
              </motion.div>

              <Divider />

              {/* Call-to-Action Summary */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.8 }}
                style={{ marginBottom: 24 }}
              >
                <motion.h5
                  style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.9 }}
                >
                   Call-to-Action
                </motion.h5>

                {/* Key Metrics */}
                {loading ? (
                  <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={24} sm={8}>
                      <ShimmerMetricCard delay={0} />
                    </Col>
                    <Col xs={24} sm={8}>
                      <ShimmerMetricCard delay={0.05} />
                    </Col>
                    <Col xs={24} sm={8}>
                      <ShimmerMetricCard delay={0.1} />
                    </Col>
                  </Row>
                ) : (
                  <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={24} sm={8}>
                      <MetricCard
                        title="High-Risk Projects"
                        value={highRiskProjects}
                        valueColor="#ff4d4f"
                        delay={1.0}
                      />
                    </Col>
                    <Col xs={24} sm={8}>
                      <MetricCard
                        title="Medium-Risk Projects"
                        value={mediumRiskProjects}
                        valueColor="#faad14"
                        delay={1.05}
                      />
                    </Col>
                    <Col xs={24} sm={8}>
                      <MetricCard
                        title="Aggregate Risk Exposure"
                        value={totalRiskExposure / 10000000}
                        prefix="₹"
                        suffix="Cr"
                        valueColor="#ff4d4f"
                        precision={2}
                        delay={1.1}
                      />
                    </Col>
                  </Row>
                )}

                {/* Top Drivers Section */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.15 }}
                  style={{ marginBottom: 24 }}
                >
                  <h6
                    style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}
                  >
                    📌 Top Risk Drivers
                  </h6>

                  {loading ? (
                    <Row gutter={[16, 16]}>
                      <Col xs={24} lg={12}>
                        <AntCard>
                          <Skeleton active paragraph={{ rows: 3 }} />
                        </AntCard>
                      </Col>
                      <Col xs={24} lg={12}>
                        <AntCard>
                          <Skeleton active paragraph={{ rows: 3 }} />
                        </AntCard>
                      </Col>
                      <Col xs={24} lg={12}>
                        <AntCard>
                          <Skeleton active paragraph={{ rows: 3 }} />
                        </AntCard>
                      </Col>
                      <Col xs={24} lg={12}>
                        <AntCard>
                          <Skeleton active paragraph={{ rows: 3 }} />
                        </AntCard>
                      </Col>
                    </Row>
                  ) : (
                    <Row gutter={[16, 16]}>
                      <Col xs={24} lg={12}>
                        <div style={{ marginBottom: 16 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom: 8,
                            }}
                          >
                            <Tag color="red">Critical</Tag>
                            <strong>Material Delays</strong>
                          </div>
                          <Table
                            size="small"
                            bordered
                            pagination={false}
                            columns={[
                              {
                                title: "Material Name",
                                dataIndex: "material",
                                key: "material",
                                width: 150,
                              },
                              {
                                title: "Qty",
                                dataIndex: "qty",
                                key: "qty",
                                width: 80,
                                align: "center",
                              },
                              {
                                title: "PO #",
                                dataIndex: "po",
                                key: "po",
                                width: 120,
                              },
                              {
                                title: "Delay (Days)",
                                dataIndex: "delay",
                                key: "delay",
                                width: 100,
                                align: "center",
                                render: (val: number) => (
                                  <Tag color="red">{val}</Tag>
                                ),
                              },
                            ]}
                            dataSource={[
                              {
                                key: "1",
                                material: "TMT Steel Bars",
                                qty: "350 ton",
                                po: "PO-2024-156",
                                delay: 7,
                              },
                              {
                                key: "2",
                                material: "Cement Grade 53",
                                qty: "200 bags",
                                po: "PO-2024-142",
                                delay: 5,
                              },
                              {
                                key: "3",
                                material: "Electrical Conduits",
                                qty: "180 units",
                                po: "PO-2024-168",
                                delay: 9,
                              },
                            ]}
                          />
                        </div>
                      </Col>

                      <Col xs={24} lg={12}>
                        <div style={{ marginBottom: 16 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom: 8,
                            }}
                          >
                            <Tag color="orange">High</Tag>
                            <strong>Rework Requirements</strong>
                          </div>
                          <Table
                            size="small"
                            bordered
                            pagination={false}
                            columns={[
                              {
                                title: "Task ID",
                                dataIndex: "taskId",
                                key: "taskId",
                                width: 80,
                              },
                              {
                                title: "Description",
                                dataIndex: "desc",
                                key: "desc",
                                width: 180,
                              },
                              {
                                title: "Qty",
                                dataIndex: "qty",
                                key: "qty",
                                width: 80,
                                align: "center",
                              },
                              {
                                title: "Status",
                                dataIndex: "status",
                                key: "status",
                                width: 100,
                                render: (val: string) => (
                                  <Tag color="orange">{val}</Tag>
                                ),
                              },
                            ]}
                            dataSource={[
                              {
                                key: "1",
                                taskId: "T-004",
                                desc: "Concrete Pouring",
                                qty: "120 sqm",
                                status: "Rework",
                              },
                              {
                                key: "2",
                                taskId: "T-201",
                                desc: "Interior Finishing",
                                qty: "85 sqm",
                                status: "Rework",
                              },
                              {
                                key: "3",
                                taskId: "T-204",
                                desc: "Marble Flooring",
                                qty: "45 sqm",
                                status: "Rework",
                              },
                            ]}
                          />
                        </div>
                      </Col>

                      <Col xs={24} lg={12}>
                        <div style={{ marginBottom: 16 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom: 8,
                            }}
                          >
                            <Tag color="red">Critical</Tag>
                            <strong>Payment Lag</strong>
                          </div>
                          <Table
                            size="small"
                            bordered
                            pagination={false}
                            columns={[
                              {
                                title: "RA Bill #",
                                dataIndex: "ra",
                                key: "ra",
                                width: 120,
                              },
                              {
                                title: "Payment Amount",
                                dataIndex: "amount",
                                key: "amount",
                                width: 130,
                                align: "right",
                                render: (val: string) => (
                                  <span style={{ fontWeight: 600 }}>{val}</span>
                                ),
                              },
                              {
                                title: "Overdue (Days)",
                                dataIndex: "overdue",
                                key: "overdue",
                                width: 120,
                                align: "center",
                                render: (val: number) => (
                                  <Tag color="red">{val}</Tag>
                                ),
                              },
                            ]}
                            dataSource={[
                              {
                                key: "1",
                                ra: "RA-2024-018",
                                amount: "₹12.5L",
                                overdue: 15,
                              },
                              {
                                key: "2",
                                ra: "RA-2024-025",
                                amount: "₹18.2L",
                                overdue: 12,
                              },
                              {
                                key: "3",
                                ra: "RA-2024-028",
                                amount: "₹8.9L",
                                overdue: 10,
                              },
                            ]}
                          />
                        </div>
                      </Col>

                      <Col xs={24} lg={12}>
                        <div style={{ marginBottom: 16 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom: 8,
                            }}
                          >
                            <Tag color="orange">High</Tag>
                            <strong>Resource Constraints</strong>
                          </div>
                          <Table
                            size="small"
                            bordered
                            pagination={false}
                            columns={[
                              {
                                title: "Resource",
                                dataIndex: "resource",
                                key: "resource",
                                width: 150,
                              },
                              {
                                title: "Issue",
                                dataIndex: "issue",
                                key: "issue",
                                width: 180,
                              },
                              {
                                title: "Impact",
                                dataIndex: "impact",
                                key: "impact",
                                width: 100,
                                render: (val: string) => (
                                  <Tag color="orange">{val}</Tag>
                                ),
                              },
                            ]}
                            dataSource={[
                              {
                                key: "1",
                                resource: "Masonry",
                                issue: "Labor shortage - skilled masons",
                                impact: "8 vacant",
                              },
                              {
                                key: "2",
                                resource: "Carpentry / Formwork / Shuttering",
                                issue: "Low productivity - 65% utilization",
                                impact: "Delayed",
                              },
                              {
                                key: "3",
                                resource: "Reinforcement / Steel Fixing",
                                issue: "Rework requirements increasing",
                                impact: "Quality",
                              },
                            ]}
                          />
                        </div>
                      </Col>
                    </Row>
                  )}
                </motion.div>

                {/* Actions Taken Section */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.2 }}
                >
                  <h6
                    style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}
                  >
                     System Actions Initiated
                  </h6>
                  {loading ? (
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={8}>
                        <ShimmerMetricCard delay={0} />
                      </Col>
                      <Col xs={24} sm={8}>
                        <ShimmerMetricCard delay={0.05} />
                      </Col>
                      <Col xs={24} sm={8}>
                        <ShimmerMetricCard delay={0.1} />
                      </Col>
                    </Row>
                  ) : (
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={8}>
                        <MetricCard
                          title="Auto Notifications"
                          value={19}
                          valueColor="#1890ff"
                          suffix="sent to stakeholders"
                          delay={1.25}
                        />
                      </Col>
                      <Col xs={24} sm={8}>
                        <MetricCard
                          title="Auto Escalations"
                          value={4}
                          valueColor="#faad14"
                          suffix="to PMO & Management"
                          delay={1.3}
                        />
                      </Col>
                      <Col xs={24} sm={8}>
                        <MetricCard
                          title="Suggested Task Reschedules"
                          value={2}
                          valueColor="#52c41a"
                          suffix="critical path updates"
                          delay={1.35}
                        />
                      </Col>
                    </Row>
                  )}
                </motion.div>
              </motion.div>
            </div>
          </BootstrapCard>
        </Block>
      </Content>

      {/* Risk Interlinking Drawer */}
      <Drawer
        title={
          <span style={{ fontSize: 18, fontWeight: 600 }}>
            Task-wise Risk Interlinking - {selectedProject?.projectName}
          </span>
        }
        placement="right"
        width="85%"
        onClose={() => setRiskDrawerVisible(false)}
        open={riskDrawerVisible}
      >
        {selectedProject && (
          <>
            <Descriptions
              bordered
              column={2}
              size="small"
              style={{ marginBottom: 24 }}
            >
              <Descriptions.Item label="Project">
                {selectedProject.projectName}
              </Descriptions.Item>
              <Descriptions.Item label="Total Delayed Tasks">
                <Tag color="red">{selectedProject.delayedTasks}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Risk Status">
                <Badge
                  status={
                    selectedProject.status === "High"
                      ? "error"
                      : selectedProject.status === "Medium"
                      ? "warning"
                      : "success"
                  }
                  text={selectedProject.status}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Risk Impact">
                {formatCurrency(
                  selectedTaskRisks.reduce(
                    (sum, task) => sum + task.monetaryImpact,
                    0
                  ),
                  "INR"
                )}
              </Descriptions.Item>
            </Descriptions>

            <Divider>Delayed Tasks with Risk Interlinking</Divider>

            <Table
              columns={taskRiskColumns}
              dataSource={selectedTaskRisks}
              rowKey="taskId"
              pagination={false}
              scroll={{ x: 1200 }}
              expandable={{
                expandedRowKeys: expandedRowKeys,
                onExpand: (expanded, record) => {
                  if (expanded) {
                    setExpandedRowKeys([record.taskId]);
                    // Simulate AI loading
                    setAiLoadingTasks((prev) => ({
                      ...prev,
                      [record.taskId]: true,
                    }));

                    // Initialize with first message
                    setAiLoadingTexts((prev) => ({
                      ...prev,
                      [record.taskId]: aiLoadingMessages[0],
                    }));

                    // Cycle through loading messages
                    let messageIndex = 0;
                    const messageInterval = setInterval(() => {
                      messageIndex =
                        (messageIndex + 1) % aiLoadingMessages.length;
                      setAiLoadingTexts((prev) => ({
                        ...prev,
                        [record.taskId]: aiLoadingMessages[messageIndex],
                      }));
                    }, 800);

                    // Complete loading after 2.5 seconds
                    setTimeout(() => {
                      clearInterval(messageInterval);
                      setAiLoadingTasks((prev) => ({
                        ...prev,
                        [record.taskId]: false,
                      }));
                    }, 2500);
                  } else {
                    setExpandedRowKeys([]);
                  }
                },
                expandedRowRender: (record) => (
                  <div
                    style={{
                      padding: "16px",
                      background:
                        "linear-gradient(135deg, #fafafa 0%, #f0f2f5 100%)",
                      borderRadius: 8,
                    }}
                  >
                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <p style={{ marginBottom: 8 }}>
                          <strong style={{ fontSize: 13, color: "#595959" }}>
                            Downstream Impact:
                          </strong>
                        </p>
                        <p style={{ fontSize: 13, color: "#262626" }}>
                          {record.downstreamImpact}
                        </p>
                      </Col>
                      <Col span={12}>
                        <p style={{ marginBottom: 8 }}>
                          <strong style={{ fontSize: 13, color: "#595959" }}>
                            Responsible Person:
                          </strong>
                        </p>
                        <Badge color="purple" text={record.responsiblePerson} />
                      </Col>
                      <Col span={24}>
                        <div
                          style={{
                            padding: 12,
                            background: "#fff",
                            borderRadius: 6,
                            border: "1px solid #d9f7be",
                          }}
                        >
                          <p
                            style={{
                              marginBottom: 8,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <RobotOutlined
                              style={{ fontSize: 16, color: "#52c41a" }}
                            />
                            <strong style={{ fontSize: 13, color: "#52c41a" }}>
                              Automated Mitigation
                            </strong>
                          </p>
                          {aiLoadingTasks[record.taskId] ? (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                padding: "12px 0",
                              }}
                            >
                              <Spin
                                indicator={
                                  <LoadingOutlined
                                    style={{ fontSize: 18, color: "#1890ff" }}
                                    spin
                                  />
                                }
                              />
                              <motion.span
                                key={
                                  aiLoadingTexts[record.taskId] ||
                                  aiLoadingMessages[0]
                                }
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0 }}
                                style={{
                                  fontSize: 13,
                                  color: "#1890ff",
                                  fontStyle: "italic",
                                }}
                              >
                                {aiLoadingTexts[record.taskId] ||
                                  aiLoadingMessages[0]}
                              </motion.span>
                            </div>
                          ) : (
                            <motion.div
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.4 }}
                            >
                              {record.automatedMitigationSteps ? (
                                <div style={{ marginTop: 8 }}>
                                  {record.automatedMitigationSteps.map(
                                    (stepData, index) => (
                                      <div
                                        key={index}
                                        style={{
                                          marginBottom: 16,
                                          paddingBottom: 12,
                                          borderBottom:
                                            index <
                                            record.automatedMitigationSteps!
                                              .length -
                                              1
                                              ? "1px dashed #e8e8e8"
                                              : "none",
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            marginBottom: 8,
                                            gap: 8,
                                          }}
                                        >
                                          {/* <Tag
                                            color="blue"
                                            style={{ fontWeight: 600 }}
                                          >
                                            Step {index + 1}
                                          </Tag> */}
                                          <strong
                                            style={{
                                              fontSize: 13,
                                              color: "#262626",
                                            }}
                                          >
                                            {stepData.step}
                                          </strong>
                                        </div>

                                        {stepData.details && (
                                          <ul
                                            style={{
                                              margin: "8px 0 0 24px",
                                              padding: 0,
                                              listStyle: "disc",
                                            }}
                                          >
                                            {stepData.details.map(
                                              (detail, idx) => (
                                                <li
                                                  key={idx}
                                                  style={{
                                                    fontSize: 12,
                                                    color: "#595959",
                                                    marginBottom: 4,
                                                    lineHeight: 1.5,
                                                  }}
                                                >
                                                  {detail}
                                                </li>
                                              )
                                            )}
                                          </ul>
                                        )}

                                        {stepData.suppliers && (
                                          <div
                                            style={{
                                              marginTop: 8,
                                              marginLeft: 24,
                                            }}
                                          >
                                            {stepData.suppliers.map(
                                              (supplier, idx) => (
                                                <div
                                                  key={idx}
                                                  style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent:
                                                      "space-between",
                                                    padding: "6px 12px",
                                                    marginBottom: 6,
                                                    background: "#f0f5ff",
                                                    borderRadius: 4,
                                                    border: "1px solid #d6e4ff",
                                                  }}
                                                >
                                                  <span
                                                    style={{
                                                      fontSize: 12,
                                                      fontWeight: 500,
                                                      color: "#262626",
                                                    }}
                                                  >
                                                    {idx + 1}. {supplier.name}
                                                  </span>
                                                  <Tag
                                                    color={
                                                      supplier.onTimePercentage >=
                                                      95
                                                        ? "green"
                                                        : supplier.onTimePercentage >=
                                                          90
                                                        ? "blue"
                                                        : "orange"
                                                    }
                                                    style={{ fontWeight: 600 }}
                                                  >
                                                    {supplier.onTimePercentage}%
                                                    On-Time
                                                  </Tag>
                                                </div>
                                              )
                                            )}
                                          </div>
                                        )}

                                        {stepData.notifications && (
                                          <ul
                                            style={{
                                              margin: "8px 0 0 24px",
                                              padding: 0,
                                              listStyle: "none",
                                            }}
                                          >
                                            {stepData.notifications.map(
                                              (notification, idx) => (
                                                <li
                                                  key={idx}
                                                  style={{
                                                    fontSize: 12,
                                                    color: "#595959",
                                                    marginBottom: 6,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 6,
                                                  }}
                                                >
                                                  <BellOutlined
                                                    style={{
                                                      fontSize: 12,
                                                      color: "#1890ff",
                                                    }}
                                                  />
                                                  {notification}
                                                </li>
                                              )
                                            )}
                                          </ul>
                                        )}

                                        {stepData.costSavings && (
                                          <div
                                            style={{
                                              marginTop: 8,
                                              marginLeft: 24,
                                              padding: "6px 12px",
                                              background: "#f6ffed",
                                              borderRadius: 4,
                                              border: "1px solid #b7eb8f",
                                            }}
                                          >
                                            <span
                                              style={{
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: "#52c41a",
                                              }}
                                            >
                                              💰 Cost Savings:{" "}
                                              {stepData.costSavings}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    )
                                  )}
                                </div>
                              ) : (
                                <div
                                  style={{
                                    fontSize: 13,
                                    color: "#262626",
                                    lineHeight: 1.6,
                                  }}
                                >
                                  <ThunderboltOutlined
                                    style={{
                                      marginRight: 8,
                                      color: "#faad14",
                                    }}
                                  />
                                  {record.automatedMitigation}
                                </div>
                              )}
                            </motion.div>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </div>
                ),
              }}
            />
          </>
        )}
      </Drawer>
    </>
  );
};

export default ProjectManagementControlTower;
